import { NavLink, useLocation, useNavigate } from "react-router-dom"
import type { LucideIcon } from "lucide-react"
import {
  Grip,
  ListPlus,
  LayoutGrid,
  Settings as Cog,
  Sun,
  Network,
  HelpCircle,
} from "lucide-react"
import type { AppShell } from "@/App"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { areaByKey, areaOfPath, type AreaKey } from "@/lib/areas"
import { PRODUCAO_NAV } from "@/areas/producao/nav"
import { ARMAZEM_NAV } from "@/areas/armazem/nav"

interface AppHeaderProps {
  shell: AppShell | null
}

/** Link do cabeçalho de uma área. Cada área declara os seus em `nav.ts`. */
export interface AreaNavItem {
  to: string
  label: string
  icon: LucideIcon
  /** Só ativo no caminho exato (para a rota base da área). */
  end?: boolean
  /** Só para Manager+ (role numérico ≥ 5). */
  managerOnly?: boolean
  /** Só para admins por Cargo (`shell.isAdmin`). O servidor volta a verificar. */
  adminOnly?: boolean
}

const ADMIN_MIN_ROLE = 5

const SIMULADOR_NAV: AreaNavItem[] = [
  { to: "/simulador", label: "Início", icon: LayoutGrid, end: true },
  { to: "/list", label: "Propostas", icon: ListPlus },
  { to: "/mapeamentos", label: "Mapeamentos", icon: Network, managerOnly: true },
  { to: "/configuracoes", label: "Configurações", icon: Cog, managerOnly: true },
  { to: "/ajuda", label: "Ajuda", icon: HelpCircle, managerOnly: true },
]

const NAV_BY_AREA: Record<AreaKey, AreaNavItem[]> = {
  simulador: SIMULADOR_NAV,
  producao: PRODUCAO_NAV,
  armazem: ARMAZEM_NAV,
  reporting: [],
}

export function AppHeader({ shell }: AppHeaderProps) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isManager = (shell?.role ?? 0) >= ADMIN_MIN_ROLE
  const area = areaOfPath(pathname)
  const areaUi = area ? areaByKey(area) : undefined
  // O botão de grelha só faz sentido com mais de uma área.
  const showGrid = area !== null && (shell?.areas?.length ?? 0) > 1
  const items = area
    ? NAV_BY_AREA[area].filter((i) => (!i.managerOnly || isManager) && (!i.adminOnly || shell?.isAdmin === true))
    : []
  // Armazém e Produção no telemóvel: cabeçalho numa linha — só os ícones até 640 px.
  const compact = area === "armazem" || area === "producao"

  return (
    <header className="border-b border-border bg-card/50 backdrop-blur-sm">
      <div
        className={cn(
          "mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8",
          compact && "px-2 py-1 sm:py-3",
        )}
      >
        {/* Nome da app removido — já aparece no header do host CRM. */}
        <nav className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:none]">
          {showGrid && (
            <button
              type="button"
              onClick={() => navigate("/")}
              title="Menu inicial"
              aria-label="Menu inicial"
              className={cn(
                "mr-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                compact && "h-12 w-12 sm:h-8 sm:w-8",
              )}
            >
              <Grip className="h-4 w-4" />
            </button>
          )}
          {areaUi && (
            <span className={cn("mr-2 shrink-0 text-sm font-semibold", compact && "hidden sm:inline")}>{areaUi.label}</span>
          )}
          {items.map((i) => (
            <HeaderLink key={i.to} to={i.to} end={i.end} icon={<i.icon className="h-4 w-4" />} compact={compact} label={i.label}>
              {i.label}
            </HeaderLink>
          ))}
        </nav>

        {area === "simulador" && (
          <div>
            <Button
              size="sm"
              onClick={() => navigate("/solar/criar")}
              disabled={!shell}
            >
              <Sun className="h-4 w-4" />
              Novo Kit Solar
            </Button>
          </div>
        )}
      </div>
    </header>
  )
}

function HeaderLink({
  to,
  end,
  icon,
  children,
  compact = false,
  label,
}: {
  to: string
  end?: boolean
  icon: React.ReactNode
  children: React.ReactNode
  /** Só o ícone abaixo de 640 px (alvo de 48 px). */
  compact?: boolean
  label?: string
}) {
  return (
    <NavLink
      to={to}
      end={end}
      aria-label={compact ? label : undefined}
      title={compact ? label : undefined}
      className={({ isActive }) =>
        cn(
          "inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
          compact && "h-12 min-w-12 justify-center px-2 sm:h-auto sm:min-w-0 sm:px-3",
          isActive
            ? "bg-accent/10 text-accent"
            : "text-muted-foreground hover:bg-muted hover:text-foreground",
        )
      }
    >
      {icon}
      {compact ? <span className="hidden sm:inline">{children}</span> : children}
    </NavLink>
  )
}
